// T · Today (QT1_Today, phone v2 F_Today; modes fresh / later / empty / done come from the real plan). The NOW card
// (first card to do) with Start / Stuck / Remove and Mark done / Edit / Later under it (tap the card to change it),
// the rest under LATER TODAY with a tick circle each (tap a row for the buttons), DONE and MOVED lists. A tick is the
// light celebration (cards.tsx tick: stamp in place, "+N", toast with Undo; a big one opens R). Stuck goes to chat.
// Remove is optimistic with an Undo toast (store). Later opens the Later sheet, Edit the F_Edit sheet.
import { useEffect, useState } from 'preact/hooks';
import type { Card } from '@shared/types';
import { C } from '@shared/content/characters';
import { isBigReward, isPerfect, localMinutes, taskShort } from '@shared/rules';
import { useApp, removeCard, editCard, toast, wrapDay } from '../../store';
import { go, useParams } from '../../router';
import { Screen, XScene, XFace, XCard, StreakPill, CardButtons, PrimaryButton, Icon, ScrollArea, IconButton } from '../../components';
import { say } from '../../lib/say';
import { NOTHING_LEFT, TODAY_DONE, TODAY_HINT } from './copy';
import { serverNow, split, timeOf, titleSize, work } from './util';
import { CardMore, EditSheet, LaterSheet, TickButton, stuckOn, tick, type CardValues } from './cards';
import s from './TodayScreen.module.css';

export function TodayScreen() {
  const st = useApp((x) => x.state);
  const params = useParams();
  const [open, setOpen] = useState<string | null>(null);
  const [later, setLater] = useState<Card | null>(null);
  const [edit, setEdit] = useState<string | null>(null);
  /** The card just ticked off here (light celebration): highlighted under DONE with its "+N", her line in the hint. */
  const [fresh, setFresh] = useState<{ id: string; pts: number } | null>(null);

  // One-shot toast handed over by S ("Moved to tomorrow", ...).
  useEffect(() => { if (params.toast) toast(params.toast); }, []);

  if (!st) return null;
  const ch = st.ch;
  const c = C[ch];
  const { items, todo, done, moved } = split(st.plan.cards);
  const now = todo[0] || null;
  const rest = todo.slice(1);
  const empty = items.length === 0;
  const allDone = !empty && todo.length === 0 && done.length > 0;
  const perfect = allDone && (st.plan.perfectGiven || isPerfect(st.plan.cards));
  const H = TODAY_HINT[ch];
  const freshCard = fresh ? done.find((x) => x.id === fresh.id) || null : null;
  // A card carried over from yesterday keeps its old time, which may have passed: it reads "Carried over" (due now,
  // never "late"), and the hint doesn't promise a check-in at a time that is gone.
  const nowMin = localMinutes(serverNow(), st.user.tz);
  const passed = (x: Card) => x.at < nowMin;
  const timeText = (x: Card) => (x.source === 'carry' && passed(x) ? 'Carried over' : x.time);
  const hint = freshCard
    ? say(TODAY_DONE[ch], { n: c.name, task: freshCard.title, year: st.today.slice(0, 4) })
    : say(empty ? H.none : allDone ? H.all : done.length ? (now && now.min <= 10 ? H.midTiny : H.mid) : now && passed(now) ? H.nextDue : H.next,
      { n: c.name, task: now ? taskShort(now.title) : null, time: now ? now.time.replace(/ [AP]M$/, '') : '' });
  const lit = st.streak.litToday;
  // Short phones (360x640): a lower next card, so its Mark done · Edit · Later row stays clear of the toast.
  const short = typeof window !== 'undefined' && window.innerHeight < 700;
  const planTime = st.tomorrowPlanTime || st.user.planTime;

  const remove = (id: string) => { setOpen(null); void removeCard(id, { from: 'today' }); };
  const stuck = (id: string) => { setOpen(null); stuckOn(id, 'today'); };
  const doTick = async (x: Card) => {
    setOpen(null);
    const rw = await tick(x, 'T', { onUndo: () => setFresh(null) });
    if (rw && !isBigReward(rw)) setFresh({ id: x.id, pts: rw.gained });
  };
  const editing = edit ? todo.find((x) => x.id === edit) || null : null;
  const saveEdit = async (v: CardValues) => {
    const id = edit;
    setEdit(null);
    if (id && await editCard(id, v)) toast('Card changed');
  };

  return (
    <Screen theme={ch} id="T" tab="today" badge={false}>
      <div class={s.scene} aria-hidden="true">
        <XScene ch={ch} stage={st.buddy.stage} mode="blur" video={false} alt="" />
        <div class={s.sceneFade} />
      </div>

      <header class={s.head}>
        <div>
          <h1 class={s.h1}>Today</h1>
          <p class={s.date}>{st.dateLabel}</p>
        </div>
        <StreakPill count={st.streak.count} lit={lit} onClick={() => go('U1')} />
      </header>

      {!empty && (
        <div class={s.progress}>
          <span class={s.progressText}>{`${done.length} of ${items.length} done`}</span>
          <div class={s.segs} role="img" aria-label={`${done.length} of ${items.length} done`}>
            {items.map((x) => <span key={x.id} class={s.seg} data-st={x.st} />)}
          </div>
        </div>
      )}

      <div class={s.hint}>
        <XFace ch={ch} e={allDone || freshCard ? 2 : done.length ? 1 : 4} s={32} ring="var(--acc)" alt="" />
        <p class={s.hintText} data-fresh={freshCard ? 'true' : 'false'}><span class="xm-sr-only">{c.name}: </span>{hint}</p>
      </div>

      <ScrollArea class={s.list} fadeTop={16}>
        {st.streak.safeNote && (
          <div class={s.safe} role="note">
            <Icon name="shield" size={22} color="#9DB7F5" />
            <span class={s.safeText}><span class={s.safeHead}>Your streak is safe</span><span>{st.streak.safeNote}</span></span>
          </div>
        )}
        {empty && (
          <div class={s.emptyCard}>
            <p class={s.emptyTitle}>No plan yet</p>
            <p class={s.emptyBody}>{`Every morning, ${c.name} turns what you say into small cards with times. Takes a minute.`}</p>
            <PrimaryButton h={54} onClick={() => go('C')} data-cta="plan" style={{ alignSelf: 'stretch' }}>{`Plan with ${c.name}`}</PrimaryButton>
          </div>
        )}

        {allDone && (
          <>
            <div class={s.doneCard}>
              <Icon name="gift" size={40} strokeWidth={1.8} color="var(--acc)" />
              <span class={s.doneCardText}>
                <span class={s.doneCardTitle}>{perfect ? 'Perfect day' : 'All done'}</span>
                <span class={s.doneCardBody}>{`${NOTHING_LEFT} Tomorrow we plan at ${planTime}.`}</span>
              </span>
            </div>
            <PrimaryButton h={52} onClick={() => { void wrapDay(); go('C', { chat: 'evening' }); }} data-cta="wrap" style={{ flex: 'none' }}>{`Wrap up the day with ${c.name}`}</PrimaryButton>
          </>
        )}

        {now && (
          <>
            <p class={s.label}>UP NEXT</p>
            <div class={s.now}>
              <div class={s.nowCard}>
                <XCard ch={ch} face="front" w={358} h={short ? 140 : 168} label={`${timeText(now).toUpperCase()} · ${now.min} MIN`} title={now.title}
                  titleSize={titleSize(now.title, 30, 27, 24)} foot="" tilt={0} />
                <button type="button" class={s.cardTap} aria-label={`Change this card: ${now.title}`} onClick={() => setEdit(now.id)} />
              </div>
              <CardButtons title={now.title} onStart={() => work(now.id)} onStuck={() => stuck(now.id)} onRemove={() => remove(now.id)} />
              <CardMore title={now.title} style={{ marginTop: -2 }} onDone={() => void doTick(now)} onEdit={() => setEdit(now.id)} onLater={() => setLater(now)} />
              {!lit && (
                <p class={s.lightHint}>
                  <Icon name="flame" size={16} color="var(--acc)" />
                  <span>{`Finish one card to light day ${st.streak.count + 1}.`}</span>
                </p>
              )}
            </div>
          </>
        )}

        {rest.length > 0 && (
          <>
            <p class={s.label} style={{ marginTop: 10 }}>LATER TODAY</p>
            {rest.map((x) => {
              const isOpen = open === x.id;
              return (
                <div key={x.id} class={s.row} data-open={isOpen ? 'true' : 'false'}>
                  <div class={s.rowTop}>
                    <TickButton done={false} title={x.title} w={48} h={64} style={{ paddingLeft: 4 }} onTick={() => void doTick(x)} />
                    <button type="button" class={s.rowMain} aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : x.id)}>
                      <span class={s.rowTime}>{timeText(x)}</span>
                      <span class={s.rowText}>
                        <span class={s.rowTitle}>{x.title}</span>
                        <span class={s.rowMin}>{`${x.min} min`}</span>
                      </span>
                    </button>
                    <IconButton icon="play" label={`Start: ${x.title}`} variant="ghost" size={44} iconSize={16} iconColor="var(--acc)" style={{ background: 'rgba(245,241,234,0.12)' }} onClick={() => work(x.id)} />
                  </div>
                  {isOpen && (
                    <div class={s.rowBtns}>
                      <CardButtons h={48} title={x.title} onStart={() => work(x.id)} onStuck={() => stuck(x.id)} onRemove={() => remove(x.id)} />
                      <div class={s.rowMore}>
                        <button type="button" class={s.laterBtn} onClick={() => { setOpen(null); setEdit(x.id); }} aria-label={`Edit: ${x.title}`}>
                          <Icon name="pencil" size={16} />
                          <span>Edit</span>
                        </button>
                        <button type="button" class={s.laterBtn} onClick={() => { setOpen(null); setLater(x); }} aria-label={`Do it later: ${x.title}`}>
                          <Icon name="clock" size={16} />
                          <span>Later</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}

        {done.length > 0 && (
          <>
            <p class={s.label} style={{ marginTop: 10 }}>DONE</p>
            {done.map((x) => (
              <div key={x.id} class={s.doneRow} data-fresh={fresh?.id === x.id ? 'true' : 'false'}>
                <span class={s.check} aria-hidden="true"><Icon name="check" size={15} strokeWidth={3} color="var(--on)" /></span>
                <span class={s.doneTitle}>{x.title}</span>
                {fresh?.id === x.id && <span class={s.pts}>{`+${fresh.pts}`}</span>}
                <span class={s.doneWhen}>{x.doneAt ? `Done ${timeOf(x.doneAt, st.user.tz)}` : 'Done'}</span>
              </div>
            ))}
          </>
        )}

        {moved.length > 0 && (
          <>
            <p class={s.label} style={{ marginTop: 10 }}>MOVED</p>
            {moved.map((x) => (
              <div key={x.id} class={s.movedRow}>
                <Icon name="arrowRight" size={20} color="#CFC8BC" />
                <span class={s.movedTitle}>{x.title}</span>
                <span class={s.movedWhen}>Tomorrow</span>
              </div>
            ))}
          </>
        )}

        <button type="button" class={s.textLink} onClick={() => go('C')}>
          <Icon name="plus" size={18} strokeWidth={2.2} color="var(--acc)" />
          <span>{`Add or change a card: just tell ${c.name}`}</span>
        </button>
        {!empty && (
          <button type="button" class={s.textLink} data-tone="muted" onClick={() => { void wrapDay(); go('C', { chat: 'evening' }); }}>
            <Icon name="moon" size={18} color="#CFC8BC" />
            <span>Wrap up the day</span>
          </button>
        )}
      </ScrollArea>

      <LaterSheet card={later} from="T" onClose={() => setLater(null)} />
      <EditSheet open={!!editing} value={editing ? { title: editing.title, time: editing.time, min: editing.min } : null} onClose={() => setEdit(null)} onSave={(v) => void saveEdit(v)} />
    </Screen>
  );
}
